import { useContext } from 'react';
import { useQueries } from 'react-query';
import { useSelector } from 'react-redux';

import { COUNTRY_CODES } from '@savgroup-front-common/constants';
import { computeOrderDetailsProductWeight } from '@savgroup-front-common/core/api/Carrier/getPickupPointByGeoCoordQuery/helpers/computeOrderDetailsProductWeight';
import { TransportPlan } from '@savgroup-front-common/core/api/Claim/getClaimGroupTransportPlanQuery';
import { getCountryTranslationSelector } from '@savgroup-front-common/core/domains/i18n/selectors';
import { formatSimpleAddressOneLine } from '@savgroup-front-common/core/formatters/address';
import { useRoutedStepsOrchestratorContext } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.context';
import {
  OrderProductSummary,
  PickupPoint,
  StrictAutocompleteOption,
} from '@savgroup-front-common/types';
import { CarrierService } from '@/api';
import { InitContext } from '@/view/app/NewLayout/InitProvider/InitProvider.context';

import { IrshStepValues } from '../../../../IrshPages.types';

export const GET_PICKUP_POINT_BY_GEO_CORD_QUERY =
  'getPickupPointByGeoCoordQuery';

export const usePickupPointOptions = ({
  availableMethod,
  searchAddress,
  isDropAtStore,
}: {
  availableMethod?: StrictAutocompleteOption<TransportPlan[]>;
  searchAddress?: {
    address?: string;
    countryCode?: string;
    distance?: number;
    postalCode?: string;
    city?: string;
  };
  isDropAtStore?: boolean;
}) => {
  const { values } = useRoutedStepsOrchestratorContext<IrshStepValues>();

  const { claims } = values;

  const claimId = claims?.at(0)?.claimId;

  const { orders } = useContext(InitContext);

  const sellerId = orders?.at(0)?.sellerId;
  const getCountry = useSelector(getCountryTranslationSelector);

  const products = values.claims?.reduce<OrderProductSummary[]>(
    (acc, claim) => {
      const orderProduct = values.order?.products.find(
        (product) => product.ownerProductId === claim.ownerProductId,
      );

      if (orderProduct) {
        return [...acc, orderProduct];
      }

      return acc;
    },
    [],
  );

  const weight = computeOrderDetailsProductWeight({ products });

  const result = useQueries(
    availableMethod?.data?.map((transportPlan) => {
      return {
        suspense: true,
        staleTime: 0,
        enabled: isDropAtStore,
        queryKey: [
          GET_PICKUP_POINT_BY_GEO_CORD_QUERY,
          {
            sellerId,
            claimId,
            searchAddress,
            transportPlanId: transportPlan.transportPlanId,
            weight,
          },
        ],
        queryFn: async () => {
          if (!sellerId || !claimId || !transportPlan) {
            return [];
          }

          const response = await CarrierService.getPickupPointByGeoCoordQuery({
            address: formatSimpleAddressOneLine(
              {
                address: searchAddress?.address ?? '',
                postalCode: searchAddress?.postalCode ?? '',
                city: searchAddress?.city ?? '',
              },
              getCountry,
            ),
            countryCode:
              searchAddress?.countryCode ?? (COUNTRY_CODES.FR as string),
            maxPickupPoint: 10,
            transportPlanId: transportPlan.transportPlanId,
            sellerId,
            claimId,
            distance: searchAddress?.distance ?? 200,
            weight,
          });

          if (response.failure) {
            return [];
          }

          const pickupPoints = response.value ?? [];

          return pickupPoints.map((pickupPoint) => ({
            value: pickupPoint.id,
            label: pickupPoint.name,
            data: {
              pickupPoint,
              transportPlan,
            },
          }));
        },
      };
    }) ?? [],
  );

  const pickupPoints = result.reduce<
    {
      value: string;
      label: string;
      data: {
        pickupPoint: PickupPoint;
        transportPlan: TransportPlan;
      };
    }[]
  >((acc, curr) => {
    if (curr.data) {
      return [...acc, ...curr.data];
    }

    return acc;
  }, []);

  const isLoading = result.some((query) => query.isLoading);

  return {
    pickupPoints,
    isLoading,
  };
};
